// components/ProgramComparisonChart.jsx
import React, { useRef, useState } from 'react';
import { ResponsiveBar } from '@nivo/bar';
import { ResponsiveRadar } from '@nivo/radar';
import { ProductsContext } from '../context/products.context';
import ExportPopover from './ExportPopover';

const ProgramComparisonChart = ({ 
  programs, 
  metric = "enrollment", 
  comparisonType = "bar",
  highlight = null 
}) => {
  const chartRef = useRef(null);
  const [selectedYear] = useState('2023-2024');

  // Prepare data based on metric
  const prepareChartData = () => {
    switch (metric) {
      case 'enrollment':
        return programs.map(p => ({
          program: p.name || p.cipName,
          cipCode: p.cipCode,
          value: p.totalEnrollment || p.enrollment || 0,
          label: `${p.totalEnrollment || p.enrollment || 0} students`
        }));
      
      case 'wages':
        return programs.map(p => ({
          program: p.name || p.cipName,
          cipCode: p.cipCode,
          value: p.avgWage || 0,
          label: (p.avgWage || 0)
        }));
      
      case 'equity':
        return programs.map(p => ({
          program: p.name || p.cipName,
          cipCode: p.cipCode,
          femalePercent: p.femalePercent || 0,
          minorityPercent: p.minorityPercent || 0,
          ellPercent: p.ellPercent || 0,
          spedPercent: p.spedPercent || 0
        }));
      
      case 'outcomes':
        return programs.map(p => ({
          program: p.name || p.cipName,
          cipCode: p.cipCode,
          completionRate: p.completionRate || 0,
          employmentRate: p.employmentRate || 0,
          averageWage: (p.avgWage || 0) / 1000, // Scale for radar
          jobOpenings: Math.min((p.jobOpenings || 0) / 10, 100) // Scale for radar
        }));
      
      default:
        return programs;
    }
  };

  const chartData = prepareChartData();

  // Find top performers
  const topPerformers = metric === 'enrollment' || metric === 'wages'
    ? [...chartData].sort((a, b) => b.value - a.value).slice(0, 3)
    : [];

  // Render appropriate chart type
  const renderChart = () => {
    if (comparisonType === 'radar' && metric === 'outcomes') {
      return (
        <ResponsiveRadar
          data={chartData}
          keys={['completionRate', 'employmentRate', 'averageWage', 'jobOpenings']}
          indexBy="program"
          maxValue={100}
          margin={{ top: 70, right: 80, bottom: 40, left: 80 }}
          curve="linearClosed"
          borderWidth={2}
          gridLevels={5}
          gridShape="circular"
          gridLabelOffset={36}
          enableDots={true}
          dotSize={10}
          dotColor={{ theme: 'background' }}
          dotBorderWidth={2}
          enableDotLabel={true}
          dotLabel="value"
          dotLabelYOffset={-12}
          colors={{ scheme: 'category10' }}
          fillOpacity={0.25}
          blendMode="multiply"
          animate={true}
          legends={[
            {
              anchor: 'top-left',
              direction: 'column',
              translateX: -50,
              translateY: -40,
              itemWidth: 80,
              itemHeight: 20,
              itemTextColor: '#999',
              symbolSize: 12,
              symbolShape: 'circle',
              effects: [
                {
                  on: 'hover',
                  style: {
                    itemTextColor: '#000'
                  }
                }
              ]
            }
          ]}
        />
      );
    }

    if (metric === 'equity' && comparisonType === 'bar') {
      return (
        <ResponsiveBar
          data={chartData}
          keys={['femalePercent', 'minorityPercent', 'ellPercent', 'spedPercent']}
          indexBy="program"
          margin={{ top: 50, right: 130, bottom: 120, left: 60 }}
          padding={0.3}
          groupMode="grouped"
          valueScale={{ type: 'linear' }}
          indexScale={{ type: 'band', round: true }}
          colors={{ scheme: 'paired' }}
          borderColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
          axisTop={null}
          axisRight={null}
          axisBottom={{
            tickSize: 5,
            tickPadding: 5,
            tickRotation: -45,
            legend: 'Program',
            legendPosition: 'middle',
            legendOffset: 80
          }}
          axisLeft={{
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
            legend: 'Percentage',
            legendPosition: 'middle',
            legendOffset: -40
          }}
          labelSkipWidth={12}
          labelSkipHeight={12}
          labelTextColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
          legends={[
            {
              dataFrom: 'keys',
              anchor: 'bottom-right',
              direction: 'column',
              justify: false,
              translateX: 120,
              translateY: 0,
              itemsSpacing: 2,
              itemWidth: 100,
              itemHeight: 20,
              itemDirection: 'left-to-right',
              itemOpacity: 0.85,
              symbolSize: 20,
              data: [
                { id: 'femalePercent', label: 'Female %' },
                { id: 'minorityPercent', label: 'Minority %' },
                { id: 'ellPercent', label: 'ELL %' },
                { id: 'spedPercent', label: 'SPED %' }
              ]
            }
          ]}
        />
      );
    }

    // Default bar chart for enrollment/wages
    return (
      <ResponsiveBar
        data={chartData}
        keys={['value']}
        indexBy="program"
        margin={{ top: 50, right: 60, bottom: 120, left: 100 }}
        padding={0.3}
        valueScale={{ type: 'linear' }}
        indexScale={{ type: 'band', round: true }}
        colors={bar => {
          if (highlight && topPerformers.some(tp => tp.cipCode === bar.data.cipCode)) {
            return '#10b981'; // green for top performers
          }
          return '#6366f1'; // default indigo
        }}
        borderRadius={4}
        borderColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
        axisTop={null}
        axisRight={null}
        axisBottom={{
          tickSize: 5,
          tickPadding: 5,
          tickRotation: -45,
          legend: 'Program',
          legendPosition: 'middle',
          legendOffset: 100
        }}
        axisLeft={{
          tickSize: 5,
          tickPadding: 5,
          tickRotation: 0,
          legend: metric === 'wages' ? 'Average Wage ($)' : 'Total Enrollment',
          legendPosition: 'middle',
          legendOffset: -80,
          format: value => metric === 'wages' ? `$${(value/1000).toFixed(0)}k` : value
        }}
        labelSkipWidth={12}
        labelSkipHeight={12}
        label={d => d.data.label}
        labelTextColor="#ffffff"
        tooltip={({ data }) => (
          <div className="bg-white p-3 shadow-lg rounded border">
            <div className="font-semibold">{data.program}</div>
            <div className="text-sm text-gray-600">CIP: {data.cipCode}</div>
            <div className="font-medium mt-1">{data.label}</div>
          </div>
        )}
      />
    );
  };

  return (
    <div className="flex flex-col w-full">
      {/* Top Performers Summary */}
      {topPerformers.length > 0 && highlight && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
          <h4 className="font-semibold text-green-900 mb-2">Top Performers</h4>
          <div className="grid grid-cols-3 gap-4">
            {topPerformers.map((program, idx) => (
              <div key={program.cipCode} className="text-sm">
                <span className="font-medium">{idx + 1}. {program.program}</span>
                <span className="text-green-700 ml-2">{program.label}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Chart */}
      <div ref={chartRef} className="w-full h-[400px]">
        {renderChart()}
      </div>

      {/* Legend/Notes */}
      {metric === 'outcomes' && (
        <div className="mt-4 text-xs text-gray-600">
          <p><strong>Metrics:</strong> Completion Rate (%), Employment Rate (%), 
          Average Wage (thousands), Job Openings (scaled)</p>
        </div>
      )}
    </div>
  );
};

export default ProgramComparisonChart;